import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { isExcludedEvaluationItem } from '../../utils/excludedEvaluationItems';
import ItemOverridePanel from '../../components/Admin/ItemOverridePanel';

interface EvaluationItem {
  item_id: string;
  department_type: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  requires_manager_approval: boolean;
  detailed_definition: string | null;
  evaluation_points: string[] | null;
  display_order: number;
  is_active: boolean;
  requires_goal: boolean;
  is_free_item: boolean;
}

interface Category {
  category_id?: string;
  category_number: number;
  category_name: string;
  display_order?: number;
  is_active?: boolean;
  items: EvaluationItem[];
  total_weight: number;
}

interface DepartmentData {
  department_type: string;
  categories: Category[];
  total_items: number;
  total_weight: number;
}

const EvaluationItemManagementPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<'tech' | 'sales' | 'admin'>('tech');
  const [viewTab, setViewTab] = useState<'base' | 'overrides'>('base');
  const [departmentData, setDepartmentData] = useState<DepartmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<EvaluationItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedItem, setSelectedItem] = useState<EvaluationItem | null>(null);
  const [itemIsCreateMode, setItemIsCreateMode] = useState(false);
  const [itemSaving, setItemSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    item_description: '',
    weight: 5,
    category_number: 1,
    detailed_definition: '',
    evaluation_points: [] as string[],
    is_active: true,
    requires_goal: false,
    is_free_item: false
  });

  // 観点マスタの操作
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryIsCreateMode, setCategoryIsCreateMode] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    category_name: '',
    category_number: '' as number | '',
    display_order: '' as number | '',
    is_active: true
  });

  useEffect(() => {
    fetchDepartmentData(currentTab);
  }, [currentTab]);

  const fetchDepartmentData = async (departmentType: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/evaluation-items/by-department/${departmentType}`,
        { params: { for_management: true } }
      );
      setDepartmentData(response.data.data);
    } catch (err) {
      console.error('評価項目データ取得エラー:', err);
      setError(extractErrorMessage(err, '評価項目データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'tech' | 'sales' | 'admin') => {
    setCurrentTab(newValue);
  };

  const handleEditClick = (item: EvaluationItem) => {
    setSelectedItem(item);
    setItemIsCreateMode(false);
    setEditFormData({
      item_description: item.item_description,
      weight: item.weight,
      category_number: item.category_number,
      detailed_definition: item.detailed_definition || '',
      evaluation_points: item.evaluation_points || [],
      is_active: item.is_active,
      requires_goal: item.requires_goal,
      is_free_item: item.is_free_item
    });
    setEditDialogOpen(true);
  };

  const handleItemCreateClick = (category: Category) => {
    setSelectedItem(null);
    setItemIsCreateMode(true);
    setEditFormData({
      item_description: '',
      weight: 5,
      category_number: category.category_number,
      detailed_definition: '',
      evaluation_points: [],
      is_active: true,
      requires_goal: false,
      is_free_item: false
    });
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedItem(null);
  };

  const handleEditFormChange = (field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteClick = (item: EvaluationItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    setError(null);
    try {
      const response = await api.delete(`/evaluation-items/${itemToDelete.item_id}`);
      const deactivated = response.data.data?.deactivated;
      toast.success(
        deactivated
          ? response.data.message || '評価項目を無効化しました'
          : '評価項目を削除しました'
      );
      await fetchDepartmentData(currentTab);
      handleDeleteDialogClose();
      if (response.data.weight_warning) {
        setError(response.data.weight_warning);
      }
    } catch (err) {
      console.error('評価項目削除エラー:', err);
      const message = extractErrorMessage(err, '評価項目の削除に失敗しました');
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!itemIsCreateMode && !selectedItem) return;

    if (!editFormData.item_description.trim()) {
      setError('評価項目の説明は必須です');
      return;
    }

    setItemSaving(true);
    setError(null);
    try {
      const response = itemIsCreateMode
        ? await api.post('/evaluation-items', {
            department_type: currentTab,
            category_number: editFormData.category_number,
            item_description: editFormData.item_description.trim(),
            weight: editFormData.weight,
            detailed_definition: editFormData.detailed_definition || null,
            evaluation_points: editFormData.evaluation_points,
            requires_goal: editFormData.requires_goal,
            is_free_item: editFormData.is_free_item
          })
        : await api.put(`/evaluation-items/${selectedItem!.item_id}`, {
            item_description: editFormData.item_description.trim(),
            weight: editFormData.weight,
            category_number: editFormData.category_number,
            detailed_definition: editFormData.detailed_definition,
            evaluation_points: editFormData.evaluation_points,
            is_active: editFormData.is_active,
            requires_goal: editFormData.requires_goal,
            is_free_item: editFormData.is_free_item
          });

      toast.success(itemIsCreateMode ? '評価項目を追加しました' : '評価項目を更新しました');

      await fetchDepartmentData(currentTab);
      handleEditDialogClose();

      // 配点合計が100点でない場合はサーバーが警告文を返す
      if (response.data.weight_warning) {
        setError(response.data.weight_warning);
      }
    } catch (err) {
      console.error('評価項目保存エラー:', err);
      const message = extractErrorMessage(
        err,
        itemIsCreateMode ? '評価項目の追加に失敗しました' : '評価項目の更新に失敗しました'
      );
      setError(message);
      toast.error(message);
    } finally {
      setItemSaving(false);
    }
  };

  const handleCategoryCreateClick = () => {
    setEditingCategory(null);
    setCategoryIsCreateMode(true);
    setCategoryFormData({
      category_name: '',
      category_number: '',
      display_order: '',
      is_active: true
    });
    setCategoryDialogOpen(true);
  };

  const handleCategoryEditClick = (category: Category) => {
    setEditingCategory(category);
    setCategoryIsCreateMode(false);
    setCategoryFormData({
      category_name: category.category_name,
      category_number: category.category_number,
      display_order: category.display_order ?? category.category_number,
      is_active: category.is_active ?? true
    });
    setCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = () => {
    if (categorySaving) return;
    setCategoryDialogOpen(false);
    setEditingCategory(null);
  };

  const handleCategorySubmit = async () => {
    const categoryName = categoryFormData.category_name.trim();
    if (!categoryName) {
      setError('観点名は必須です');
      return;
    }

    setCategorySaving(true);
    setError(null);
    try {
      if (categoryIsCreateMode) {
        await api.post('/evaluation-categories', {
          department_type: currentTab,
          category_name: categoryName,
          category_number: categoryFormData.category_number === '' ? undefined : Number(categoryFormData.category_number),
          display_order: categoryFormData.display_order === '' ? undefined : Number(categoryFormData.display_order)
        });
        toast.success('観点を追加しました');
      } else if (editingCategory?.category_id) {
        const response = await api.put(`/evaluation-categories/${editingCategory.category_id}`, {
          category_name: categoryName,
          category_number: categoryFormData.category_number === '' ? undefined : Number(categoryFormData.category_number),
          display_order: categoryFormData.display_order === '' ? undefined : Number(categoryFormData.display_order),
          is_active: categoryFormData.is_active
        });
        toast.success(response.data.message || '観点を更新しました');
      }
      await fetchDepartmentData(currentTab);
      setCategoryDialogOpen(false);
      setEditingCategory(null);
    } catch (err) {
      console.error('観点保存エラー:', err);
      const message = extractErrorMessage(err, '観点の保存に失敗しました');
      setError(message);
      toast.error(message);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleCategoryDeleteClick = async (category: Category) => {
    if (!category.category_id) return;
    if (!window.confirm(`観点「${category.category_name}」を削除しますか？この操作は取り消せません。`)) return;

    setError(null);
    try {
      await api.delete(`/evaluation-categories/${category.category_id}`);
      toast.success('観点を削除しました');
      await fetchDepartmentData(currentTab);
    } catch (err) {
      console.error('観点削除エラー:', err);
      const message = extractErrorMessage(err, '観点の削除に失敗しました');
      setError(message);
      toast.error(message);
    }
  };

  const getDepartmentName = (type: string) => {
    switch (type) {
      case 'tech': return '技術部門';
      case 'sales': return '営業部門';
      case 'admin': return '管理部門';
      default: return type;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          評価項目マスタ管理
        </Typography>
        <Typography variant="body2" color="text.secondary">
          各部門の観点と評価項目、職級・役職別の上書きを確認・編集します
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="部門タブ">
          <Tab label="技術部門" value="tech" />
          <Tab label="営業部門" value="sales" />
          <Tab label="管理部門" value="admin" />
        </Tabs>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={viewTab}
          onChange={(_e, value: 'base' | 'overrides') => setViewTab(value)}
          aria-label="表示切替タブ"
        >
          <Tab label="観点と評価項目" value="base" />
          <Tab label="職級・役職別の上書き" value="overrides" />
        </Tabs>
      </Paper>

      {viewTab === 'overrides' && <ItemOverridePanel departmentType={currentTab} />}

      {viewTab === 'base' && departmentData && (
        <Box>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {getDepartmentName(departmentData.department_type)}の評価項目概要
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  総項目数
                </Typography>
                <Typography variant="h5">
                  {departmentData.total_items} 項目
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  総配点
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: departmentData.total_weight === 100 ? 'success.main' : 'error.main',
                    fontWeight: 'bold'
                  }}
                >
                  {departmentData.total_weight} 点
                </Typography>
                {departmentData.total_weight !== 100 && (
                  <Chip
                    label="要調整"
                    size="small"
                    color="warning"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  観点数
                </Typography>
                <Typography variant="h5">
                  {departmentData.categories.length} 観点
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleCategoryCreateClick}
                >
                  観点を追加
                </Button>
              </Box>
            </Box>
            {departmentData.total_weight !== 100 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                重みの合計が100点になっていません。評価の正確性のため、100点に調整することを推奨します。
              </Alert>
            )}
          </Paper>

          {departmentData.categories.map((category) => (
            <Accordion key={category.category_number} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6">
                    観点{category.category_number}: {category.category_name}
                  </Typography>
                  <Chip
                    label={`${category.total_weight}点`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`${category.items.length}項目`}
                    variant="outlined"
                    size="small"
                  />
                  {category.is_active === false && (
                    <Chip label="無効" size="small" color="default" />
                  )}
                  {category.category_id && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleItemCreateClick(category)}
                      >
                        項目を追加
                      </Button>
                      <Tooltip title="観点を編集">
                        <IconButton size="small" onClick={() => handleCategoryEditClick(category)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          category.items.length > 0
                            ? '評価項目が紐づいているため削除できません'
                            : '観点を削除'
                        }
                      >
                        <IconButton
                          size="small"
                          color="error"
                          disabled={category.items.length > 0}
                          onClick={() => handleCategoryDeleteClick(category)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>項目No</TableCell>
                        <TableCell>評価項目</TableCell>
                        <TableCell align="center">重み</TableCell>
                        <TableCell align="center">状態</TableCell>
                        <TableCell align="center">操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.items.map((item) => (
                        <TableRow key={item.item_id} hover>
                          <TableCell>{item.item_number}</TableCell>
                          <TableCell>
                            {item.item_description}
                            {item.is_free_item && (
                              <Chip label="フリー項目" size="small" color="info" sx={{ ml: 1 }} />
                            )}
                            {item.requires_goal && (
                              <Chip label="目標必須" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                            {isExcludedEvaluationItem(item.item_id) && (
                              <Chip
                                label="評価フロー除外"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.weight}
                              size="small"
                              color={item.weight === 10 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.is_active ? '有効' : '無効'}
                              size="small"
                              color={item.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(item)}
                              aria-label="編集"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(item)}
                              aria-label="削除"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* 観点の追加・編集ダイアログ */}
      <Dialog open={categoryDialogOpen} onClose={handleCategoryDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {categoryIsCreateMode
            ? `観点の追加（${getDepartmentName(currentTab)}）`
            : `観点の編集: ${editingCategory?.category_name}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              required
              label="観点名"
              value={categoryFormData.category_name}
              onChange={(e) =>
                setCategoryFormData((prev) => ({ ...prev, category_name: e.target.value }))
              }
              placeholder="例: 顧客課題解決への貢献"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              label="観点番号"
              value={categoryFormData.category_number}
              onChange={(e) =>
                setCategoryFormData((prev) => ({
                  ...prev,
                  category_number: e.target.value === '' ? '' : Number(e.target.value)
                }))
              }
              helperText={
                categoryIsCreateMode
                  ? '未入力の場合は既存の最大番号＋1になります'
                  : '番号を変更すると、この観点に属する評価項目の観点番号も自動で変わります'
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              label="表示順"
              value={categoryFormData.display_order}
              onChange={(e) =>
                setCategoryFormData((prev) => ({
                  ...prev,
                  display_order: e.target.value === '' ? '' : Number(e.target.value)
                }))
              }
              helperText="未入力の場合は末尾に追加されます"
              sx={{ mb: 2 }}
            />
            {!categoryIsCreateMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryFormData.is_active}
                    onChange={(e) =>
                      setCategoryFormData((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                }
                label="有効（無効にすると項目の追加時に選択できなくなります）"
              />
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              観点名を変更すると、この観点に属する評価項目マスタの観点名も更新されます。
              すでに作成済みの評価データは、評価基準の確定値として旧名称を保持します。
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCategoryDialogClose} disabled={categorySaving}>
            キャンセル
          </Button>
          <Button onClick={handleCategorySubmit} variant="contained" disabled={categorySaving}>
            {categorySaving ? '処理中...' : categoryIsCreateMode ? '追加' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>評価項目の削除</DialogTitle>
        <DialogContent>
          {itemToDelete && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                以下の評価項目を削除しますか？
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {itemToDelete.item_number}: {itemToDelete.item_description}
              </Typography>
              <Alert severity="warning">
                過去の評価データで使用されている場合は、物理削除されず「無効」になります。
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? '処理中...' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 項目の追加・編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {itemIsCreateMode
            ? `評価項目の追加（${getDepartmentName(currentTab)}）`
            : '評価項目の編集'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="項目番号"
              value={itemIsCreateMode ? '（保存時に自動採番されます）' : selectedItem?.item_number || ''}
              disabled
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="item-category-label">観点</InputLabel>
              <Select
                labelId="item-category-label"
                label="観点"
                value={editFormData.category_number}
                onChange={(e) => handleEditFormChange('category_number', Number(e.target.value))}
              >
                {(departmentData?.categories || [])
                  .filter((c) => c.is_active !== false)
                  .map((c) => (
                    <MenuItem key={c.category_number} value={c.category_number}>
                      観点{c.category_number}: {c.category_name}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>
                {itemIsCreateMode
                  ? 'この項目を属させる観点を選びます'
                  : '観点を変更すると、この項目は選んだ観点へ移ります'}
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              required
              label="評価項目の説明"
              value={editFormData.item_description}
              onChange={(e) => handleEditFormChange('item_description', e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="item-weight-label">配点</InputLabel>
              <Select
                labelId="item-weight-label"
                label="配点"
                value={editFormData.weight}
                onChange={(e) => handleEditFormChange('weight', Number(e.target.value))}
              >
                {[5, 10, 20].map((w) => (
                  <MenuItem key={w} value={w}>
                    {w} 点
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                部門全体の配点合計が100点になるようにします。評価対象から外す場合は配点0ではなく「無効」にします
              </FormHelperText>
            </FormControl>
            <TextField
              fullWidth
              label="詳細定義"
              value={editFormData.detailed_definition}
              onChange={(e) => handleEditFormChange('detailed_definition', e.target.value)}
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="評価のポイント"
              value={editFormData.evaluation_points.join('\n')}
              onChange={(e) => handleEditFormChange('evaluation_points', e.target.value.split('\n').filter(p => p.trim()))}
              multiline
              rows={4}
              helperText="各行に1つずつポイントを入力してください"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.requires_goal}
                    onChange={(e) => handleEditFormChange('requires_goal', e.target.checked)}
                  />
                }
                label="目標・達成基準の設定を必須にする"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -0.5, mb: 1 }}>
                必須にすると、被評価者は目標設定でこの項目の目標と達成基準を入力しないと承認申請できません。
                職級・役職ごとに変えたい場合は「職級・役職別の上書き」タブで設定します。
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.is_free_item}
                    onChange={(e) => handleEditFormChange('is_free_item', e.target.checked)}
                  />
                }
                label="フリー項目とする（本人が内容を自由に設定する項目）"
              />

              {!itemIsCreateMode && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.is_active}
                      onChange={(e) => handleEditFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="有効（無効にすると評価対象から外れます）"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} disabled={itemSaving}>
            キャンセル
          </Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary" disabled={itemSaving}>
            {itemSaving ? '処理中...' : itemIsCreateMode ? '追加' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EvaluationItemManagementPage;
